/**
 * The widget embed postMessage protocol — a FOREVER contract (features/booking-widget-embed.md R1,
 * R6). Venues paste the snippet once; this message surface can only ever grow additively, never
 * break. Shared by BOTH sides: the loader (`embed/v1.js`, host-page realm) and the app (inside the
 * cross-origin iframe). Being plain TypeScript with zero runtime deps, it bundles into the
 * dependency-free loader AND the React app from a single source, so the two can never drift.
 *
 * Security posture (R6 — the host page must NOT be able to observe what the diner types):
 *  - The namespace + version discriminate our frames from any other `postMessage` chatter.
 *  - Payloads are LAYOUT-ONLY. The `WidgetMessage` union has exactly one data field, `height`
 *    (a number). There is deliberately NO string/object field that could carry booking form input
 *    across the origin boundary — so "no PII crosses" is a COMPILE-TIME property, and `parseMessage`
 *    re-projects onto the whitelist at RUNTIME (any extra attacker-injected field is dropped).
 *  - Both sides validate `event.origin` AND `event.source`; neither ever posts with `targetOrigin:'*'`.
 */

export const NS = "rsv" as const;
export const VERSION = 1 as const;

/** The closed set of message types. Layout/lifecycle only — never a data channel. */
export const MESSAGE_TYPES = ["ready", "resize", "open", "close"] as const;
export type WidgetMessageType = (typeof MESSAGE_TYPES)[number];

/**
 * The wire message. `height` is the ONLY payload field and it is a number (CSS pixels) — present
 * only on `resize`. Adding any string field here would be a review-flag: it would open a PII path.
 */
export interface WidgetMessage {
  ns: typeof NS;
  v: typeof VERSION;
  type: WidgetMessageType;
  height?: number;
}

const TYPE_SET: ReadonlySet<string> = new Set(MESSAGE_TYPES);

/**
 * Parse an untrusted `MessageEvent.data` into a `WidgetMessage`, or `null` if it is not one of ours.
 *
 * Re-projects onto the whitelist `{ns, v, type, height}` — a message carrying extra fields (e.g. an
 * injected `payload`/`token`) is either rejected or stripped, so nothing outside the layout contract
 * can ride across the boundary. `height` is accepted only as a finite, non-negative number.
 */
export function parseMessage(data: unknown): WidgetMessage | null {
  if (typeof data !== "object" || data === null) return null;
  const record = data as Record<string, unknown>;
  if (record.ns !== NS || record.v !== VERSION) return null;
  if (typeof record.type !== "string" || !TYPE_SET.has(record.type)) return null;

  const message: WidgetMessage = { ns: NS, v: VERSION, type: record.type as WidgetMessageType };

  if (record.type === "resize") {
    const { height } = record;
    if (typeof height !== "number" || !Number.isFinite(height) || height < 0) return null;
    message.height = height;
  }
  return message;
}

/** A concrete browser origin (never `*`, never `null`) — the only thing we will `postMessage` to. */
export function isConcreteOrigin(origin: string | null | undefined): origin is string {
  if (!origin || origin === "*" || origin === "null") return false;
  try {
    const url = new URL(origin);
    // Origin form only: scheme + host [+ port], no path/query/hash.
    return (
      (url.protocol === "https:" || url.protocol === "http:") &&
      `${url.protocol}//${url.host}` === origin
    );
  } catch {
    return false;
  }
}

/**
 * Guard an inbound event: it must come from `expectedSource` (identity check, blocks a sibling
 * frame) AND from `expectedOrigin` (blocks a spoofed origin). A null/absent expected source is a
 * hard reject (never "skip the check"), and a non-concrete expected origin rejects everything.
 * Either mismatch → ignore silently. Returns the parsed message when trusted, else `null`.
 */
export function acceptMessage(
  event: Pick<MessageEvent, "data" | "origin" | "source">,
  expectedSource: MessageEventSource | null,
  expectedOrigin: string,
): WidgetMessage | null {
  if (!expectedSource || event.source !== expectedSource) return null;
  if (!isConcreteOrigin(expectedOrigin) || event.origin !== expectedOrigin) return null;
  return parseMessage(event.data);
}

/**
 * Post a message to a target window at a CONCRETE origin. Refuses `*` — a wildcard target would let
 * any page in the frame chain read the message. No-ops (returns false) on an invalid origin rather
 * than leaking to `*`.
 */
export function postMessageTo(
  target: Pick<Window, "postMessage">,
  origin: string,
  message: WidgetMessage,
): boolean {
  if (!isConcreteOrigin(origin)) return false;
  target.postMessage(message, origin);
  return true;
}

/** Build a well-formed message (keeps every construction on the same code path). */
export function makeMessage(type: WidgetMessageType, height?: number): WidgetMessage {
  return type === "resize" && typeof height === "number"
    ? { ns: NS, v: VERSION, type, height }
    : { ns: NS, v: VERSION, type };
}
